// =============================================================================
// EntityOrgChart — the legal-entity ownership/subsidiary structure.
//
// PRECEDENCE: an entity whose CURRENT Operating Agreement has been read and
// yields ONE clear controlling internal member (see api/src/entityOwnership.js)
// draws its tree edge FROM THAT — that is the "based on the current operating
// agreement" ask. Everything else falls back to Sage's own location hierarchy
// (dbo.SageLocations.ParentID — a GL/accounting roll-up, not a legal fact) the
// way this chart originally shipped. Each node says which one produced its
// edge. Non-regressive: an entity with no OA filed, or an OA read with an
// unresolved tie for control, renders exactly as it did before.
//
// A FOREST, not a single tree: most of this book is independent LLCs with no
// recorded parent/controlling member, and that is the expected shape, not a
// gap — a connecting line is a fact somebody (a person, or the OA itself)
// stated, nothing more.
//
// No new chart library: a rooted forest with a handful of levels renders
// perfectly well as an indented, collapsible tree, and this screen already
// lives behind Settings (Admin-only nav gating, Admin-only on the API too,
// since these are governance/ownership facts).
// =============================================================================
import { useState } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { Btn, Spinner, ErrorMsg, Badge, EmptyState, useToast } from '../ui';

const SOURCE_LABEL = {
  'operating-agreement': 'from its Operating Agreement',
  'sage-hierarchy': "from Sage's location hierarchy",
};

const MembersList = ({ members }) => (
  <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${B.border}` }}>
    {members.map((m, i) => (
      <div key={i} style={{ fontSize: 11, color: B.textLt, display: 'flex', gap: 6, alignItems: 'baseline' }}>
        <span style={{ color: B.text }}>{m.memberName}</span>
        {m.ownershipPercent != null && <span>{m.ownershipPercent}%</span>}
        {m.isSelf && <span style={{ color: B.amber }}>(names itself — ignored)</span>}
        {!m.isSelf && !m.isInternal && <span>· outside owner</span>}
      </div>
    ))}
  </div>
);

const OrgChartNode = ({ node, depth = 0 }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const hasChildren = (node.children || []).length > 0;
  const members = node.members || [];

  return (
    <div style={{ marginLeft: depth === 0 ? 0 : 22, borderLeft: depth > 0 ? `2px solid ${B.border}` : 'none', paddingLeft: depth > 0 ? 12 : 0 }}>
      <div style={{ padding: '8px 10px', marginBottom: 6, background: B.surface, border: `1px solid ${B.border}`, borderRadius: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {hasChildren ? (
            <button onClick={() => setCollapsed(c => !c)} aria-label={collapsed ? 'Expand' : 'Collapse'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, width: 14, fontSize: 12, color: B.textMd, lineHeight: 1 }}>
              {collapsed ? '▸' : '▾'}
            </button>
          ) : <span style={{ width: 14 }} />}
          <Badge color={B.navy}>{node.entityCode}</Badge>
          <span style={{ fontWeight: 600, fontSize: 13, color: B.text }}>{node.name}</span>
          {node.documentCount > 0 && (
            <Badge color={B.tealDk}>{node.documentCount} filed doc{node.documentCount === 1 ? '' : 's'}</Badge>
          )}
          {node.ownershipSource && (
            <span style={{ fontSize: 11, color: node.ownershipSource === 'operating-agreement' ? B.tealDk : B.textLt }}>
              {SOURCE_LABEL[node.ownershipSource] || node.ownershipSource}
              {node.ownershipAsOfDate ? ` · as of ${node.ownershipAsOfDate}` : ''}
            </span>
          )}
          {node.orphaned && <Badge color={B.amber}>recorded parent no longer in the roster</Badge>}
          {node.cycleBroken && <Badge color={B.red}>parent chain loops back on itself</Badge>}
          {node.unresolvedParentCode && (
            <span style={{ fontSize: 11, color: B.textLt }}>
              recorded parent (not a declared entity): {node.unresolvedParentCode}
            </span>
          )}
          {members.length > 0 && (
            <button onClick={() => setShowMembers(s => !s)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 11, color: B.navy, textDecoration: 'underline' }}>
              {showMembers ? 'Hide' : 'Show'} {members.length} member{members.length === 1 ? '' : 's'}
            </button>
          )}
        </div>
        {showMembers && members.length > 0 && <MembersList members={members} />}
      </div>
      {hasChildren && !collapsed && node.children.map(c => <OrgChartNode key={c.id} node={c} depth={depth + 1} />)}
    </div>
  );
};

export const EntityOrgChart = () => {
  const { toast } = useToast();
  const { data, loading, error, reload } = useApi(() => api.get('sage/entities/org-chart'), []);
  const [extracting, setExtracting] = useState(false);

  const extractAll = async () => {
    setExtracting(true);
    try {
      const res = await api.post('sage/entities/ownership/sweep', {});
      if (!res.available) {
        toast.error(res.reason || 'Could not read the operating agreements.');
      } else if (res.attempted === 0) {
        toast.success('Nothing to read — every filed current Operating Agreement has already been read. File one, or re-file one, to make it eligible again.');
      } else {
        toast.success(`Read ${res.attempted} Operating Agreement${res.attempted === 1 ? '' : 's'} — ${res.ready} with ownership found, ${res.unknown} stated none, ${res.errored} failed.`);
        reload();
      }
    } catch (e) { toast.error(e.message); }
    finally { setExtracting(false); }
  };

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} onRetry={reload} />;

  const roots = data?.roots || [];
  const ownershipAvailable = data?.ownershipAvailable !== false;

  if (roots.length === 0) {
    return <EmptyState title="No entities yet" message="Import the Sage entity list from Settings → Sage Import → Source Entities first." />;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: B.textLt, maxWidth: 620 }}>
          {data.entityCount} legal entities. A connecting line comes from either an entity&apos;s own <strong>current
          Operating Agreement</strong> (filed under Documents and read for its membership structure — the source
          this chart prefers) or, failing that, <strong>Sage&apos;s location hierarchy</strong>
          (dbo.SageLocations.ParentID, a GL/accounting roll-up used as a fallback). Most entities have neither
          recorded and are independent roots — that is the expected shape for a book of largely-independent LLCs,
          not a gap.
        </div>
        {ownershipAvailable && (
          <Btn size="sm" variant="secondary" onClick={extractAll} loading={extracting}>
            Read current Operating Agreements now
          </Btn>
        )}
      </div>
      {!ownershipAvailable && (
        <div style={{ fontSize: 12, color: B.amber, marginBottom: 12 }}>
          Reading operating agreements for the chart isn&apos;t available yet on this database — the chart below is
          Sage&apos;s location hierarchy only.
        </div>
      )}
      {roots.map(r => <OrgChartNode key={r.id} node={r} />)}
    </div>
  );
};
